import { useState } from 'react';
import { CheckCircle, GlobeHemisphereWest, User, UsersThree } from '@phosphor-icons/react';
import { InnenKarte, KartenTitel, ToastPill, useToastPill } from '../../../../../components/immobilien/ui';
import { initialen } from '../../../../../lib/immobilien/format';
import { setzeZustaendig, type ZustaendigOption } from '../../../../../lib/immobilien/useObjektListe';
import { meldeObjektGeaendert } from '../../../../../lib/immobilien/useObjektKontext';
import { useObjektSeite } from '../ObjektSeitenKontext';

/** Panel „Zuständigkeit“: Global, ein Mitarbeiter oder ein Team – gleiche Schreiblogik wie in der Liste. */
export function ZustaendigPanel() {
  const { daten, neuLaden } = useObjektSeite();
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const o = daten.objekt;
  const aktuellId = o.zustaendig_id ?? null;

  const waehle = async (ziel: ZustaendigOption | null) => {
    const schluessel = ziel?.id ?? 'global';
    setLaeuft(schluessel);
    try {
      await setzeZustaendig(o.id, ziel);
      zeige(`Zuständig: ${ziel ? ziel.name : 'Global'}`);
      neuLaden('objekt');
      meldeObjektGeaendert(o.id);
    } catch (e) {
      zeige(`Nicht gespeichert: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    } finally {
      setLaeuft(null);
    }
  };

  const karte = (schluessel: string, name: string, sub: string, an: boolean, inhalt: React.ReactNode, onClick: () => void) => (
    <button key={schluessel} type="button" aria-pressed={an} disabled={laeuft !== null} onClick={onClick}
      className="immo-hover-flaeche flex items-center text-left cursor-pointer min-w-0"
      style={{ gap: 12, padding: 12, borderRadius: 18, border: 'none', background: an ? 'var(--acc-soft)' : 'var(--bg)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', opacity: laeuft !== null && laeuft !== schluessel ? 0.6 : 1 }}>
      <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: '50%', background: an ? 'var(--acc-fill)' : 'var(--fill2)', color: an ? 'var(--acc-ink)' : 'var(--ink)', fontSize: 13, fontWeight: 700 }}>{inhalt}</span>
      <span className="flex flex-col flex-1 min-w-0"><span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{name}</span><span style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span></span>
      {an && <CheckCircle weight="fill" size={20} color="var(--acc)" />}
    </button>
  );

  const mitarbeiter = daten.zustaendigOptionen.filter(z => z.typ === 'mitarbeiter');
  const teams = daten.zustaendigOptionen.filter(z => z.typ === 'team');
  const raster = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 8 } as const;

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte>
        <KartenTitel icon={GlobeHemisphereWest}>Allgemein</KartenTitel>
        <div style={raster}>
          {karte('global', 'Global', 'Alle Mitarbeitenden', !aktuellId, <GlobeHemisphereWest weight="fill" size={20} />, () => { void waehle(null); })}
        </div>
      </InnenKarte>
      <InnenKarte>
        <KartenTitel icon={User} rechts={`${mitarbeiter.length}`}>Mitarbeiter</KartenTitel>
        <div style={raster}>
          {mitarbeiter.map(m => karte(m.id, m.name, 'Mitarbeiter', aktuellId === m.id, initialen(m.name), () => { void waehle(m); }))}
        </div>
      </InnenKarte>
      {teams.length > 0 && (
        <InnenKarte>
          <KartenTitel icon={UsersThree} rechts={`${teams.length}`}>Teams</KartenTitel>
          <div style={raster}>
            {teams.map(t => karte(t.id, t.name, 'Team', aktuellId === t.id, <UsersThree weight="fill" size={18} />, () => { void waehle(t); }))}
          </div>
        </InnenKarte>
      )}
    </>
  );
}
